import React from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faUsers, faClock, faBan, faBell, faEdit, faTrash, faSearch, faFilter, faDownload, faUpload } from '@fortawesome/free-solid-svg-icons';

const QuickActionsPage: React.FC = () => {
  const breadcrumb = [{ label: 'Dashboard' }, { label: 'Rychlé akce' }];

  const quickActions = [
    { title: 'Nová rezervace', description: 'Vytvořit novou rezervaci', icon: faCalendarCheck, color: 'bg-green-500', href: '/app/new-reservation' },
    { title: 'Nový zákazník', description: 'Přidat nového zákazníka', icon: faUsers, color: 'bg-blue-500', href: '/app/new-customer' },
    { title: 'Blokovat čas', description: 'Zablokovat časový slot', icon: faBan, color: 'bg-red-500', href: '/app/block-time' },
    { title: 'Čekací lista', description: 'Spravovat čekací listu', icon: faClock, color: 'bg-yellow-500', href: '/app/waiting-list' },
    { title: 'Hromadné oznámení', description: 'Poslat oznámení zákazníkům', icon: faBell, color: 'bg-purple-500', href: '/app/bulk-notification' },
    { title: 'Export dat', description: 'Exportovat data do CSV', icon: faDownload, color: 'bg-indigo-500', href: '/app/export' },
  ];

  const recentActions = [
    { action: 'Vytvořena rezervace', details: 'Jana Nováková - Střih a foukaná', time: 'před 5 minutami', user: 'Anna Svobodová' },
    { action: 'Přidán zákazník', details: 'Pavel Novotný', time: 'před 12 minutami', user: 'Petr Novák' },
    { action: 'Zablokován čas', details: '14:00-15:00 - Dovolená', time: 'před 20 minutami', user: 'Marie Krásná' },
    { action: 'Odesláno oznámení', details: 'Hromadné SMS - 25 zákazníků', time: 'před 30 minutami', user: 'Jana Nová' },
    { action: 'Export dat', details: 'Měsíční report - listopad 2024', time: 'před 1 hodinou', user: 'Tomáš Vlasák' },
  ];

  const shortcuts = [
    { key: 'Ctrl+N', action: 'Nová rezervace' },
    { key: 'Ctrl+C', action: 'Nový zákazník' },
    { key: 'Ctrl+B', action: 'Blokovat čas' },
    { key: 'Ctrl+E', action: 'Export dat' },
    { key: 'Ctrl+F', action: 'Hledat' },
    { key: 'Ctrl+S', action: 'Uložit' },
  ];

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Rychlé akce" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Quick Actions Grid */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Rychlé akce</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickActions.map((action, index) => (
                <button key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md hover:transform hover:-translate-y-1 transition-all text-left">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-lg ${action.color}`}>
                      <FontAwesomeIcon icon={action.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">{action.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{action.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Actions */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Nedávné akce</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {recentActions.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={faEdit} className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{item.action}</p>
                        <p className="text-sm text-gray-600 truncate">{item.details}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {item.user} • {item.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Keyboard Shortcuts */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Klávesové zkratky</h3>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  {shortcuts.map((shortcut, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">{shortcut.action}</span>
                      <span className="px-2 py-1 bg-gray-100 text-xs font-mono text-gray-700 rounded">{shortcut.key}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Hromadné akce</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors">
                  <FontAwesomeIcon icon={faBell} className="w-6 h-6 text-gray-400 mr-3" />
                  <span className="text-gray-600">Hromadné SMS</span>
                </button>
                <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="w-6 h-6 text-gray-400 mr-3" />
                  <span className="text-gray-600">Export zákazníků</span>
                </button>
                <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors">
                  <FontAwesomeIcon icon={faUpload} className="w-6 h-6 text-gray-400 mr-3" />
                  <span className="text-gray-600">Import dat</span>
                </button>
                <button className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-red-500 hover:bg-red-50 transition-colors">
                  <FontAwesomeIcon icon={faTrash} className="w-6 h-6 text-gray-400 mr-3" />
                  <span className="text-gray-600">Hromadné mazání</span>
                </button>
              </div>
            </div>
          </section>

          {/* Search and Filter */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Rychlé vyhledávání</h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Hledat zákazníky, rezervace..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                </div>
                <button className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faFilter} className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-600">Filtry</span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default QuickActionsPage;
